import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
// ⚠️ Deep import of the .desktop variant is REQUIRED (ledger hazard 6): a bare folder import
// typechecks against index.native, whose LoginSlice exports a different surface.
import { userLogout, supportLogout } from "../LoginSlice";
import {
  sharedInitialState,
  sharedReducers,
  type SharedPosSettingsState,
} from "./shared";

interface Props extends SharedPosSettingsState {
  pinnedChannelData: [];
  tableViewPinnedChannel: [];
}

const initialState: Props = {
  ...sharedInitialState,
  pinnedChannelData: [],
  tableViewPinnedChannel: [],
};

export const PosSettingsSlice = createSlice({
  name: "posSettings",
  initialState,
  reducers: {
    ...sharedReducers,
    // Web takes `(state, actions)`; mobile's is 0-arity. Both bodies are empty — the action
    // exists only for the saga's `takeEvery(savePosSettings.type, ...)`. Arity preserved per
    // platform (hazard 14); note the param name `actions` is web's own spelling.
    savePosSettings: (state: Props, actions: PayloadAction<any>) => {},
    setModuleSettingsLoaded: (state: Props, action: PayloadAction<any>) => {
      state.defaultModuleSettings = {
        ...state.defaultModuleSettings,
        hasLoaded: action.payload
      }
    },
    updatePosSettingsSlice: (state: Props, action: PayloadAction<any>) => {
      return action.payload;
    },
    setPinnedChannels: (state: Props, action: PayloadAction<any>) => {
      state.pinnedChannelData = action.payload || [];
    },
    setTableViewPinnedChannel: (state: Props, action: PayloadAction<any>) => {
      state.tableViewPinnedChannel = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ⚠️ EMPTY ON PURPOSE — verbatim from apps/web. The case IS registered (so the action is
      // handled and produces a new draft) but the body is commented out there:
      //   `// state.defaultModuleSettings = {}`
      // Preserved exactly; "fixing" it would change web's logout behaviour.
      .addCase(userLogout, (state) => {
        // state.defaultModuleSettings = {}
      })
      .addCase(supportLogout, () => initialState);
  },
});

export const {
  savePosSettings,
  saveDefaultModuleSettings,
  updatePosSettingsSlice,
  setModuleSettingsLoaded,
  setPinnedChannels,
  setTableViewPinnedChannel,
} = PosSettingsSlice.actions

export default PosSettingsSlice.reducer;
